import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToSection } from "../lib/scroll";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Features", id: "features" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

const saveScroll = () =>
  sessionStorage.setItem("cdm-scroll", String(window.scrollY));

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/[0.06] bg-[#04070F]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Logo onClick={() => go("home")} />

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-300 transition-colors hover:text-emerald-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            data-testid="nav-login"
            to="/app"
            onClick={saveScroll}
            className="btn-outline px-5 py-2.5 text-sm font-semibold"
          >
            Login
          </Link>
          <Link
            data-testid="nav-create-order"
            to="/app"
            onClick={saveScroll}
            className="btn-primary px-5 py-2.5 text-sm"
          >
            Create Order
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-colors hover:border-emerald-400/60 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/[0.06] bg-[#04070F]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-slate-200 transition-colors hover:bg-white/5 hover:text-emerald-300"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex flex-col gap-3 px-1 pb-2">
                <Link
                  data-testid="mobile-nav-login"
                  to="/app"
                  onClick={() => {
                    saveScroll();
                    setOpen(false);
                  }}
                  className="btn-outline w-full py-3 text-sm font-semibold"
                >
                  Login
                </Link>
                <Link
                  data-testid="mobile-nav-create-order"
                  to="/app"
                  onClick={() => {
                    saveScroll();
                    setOpen(false);
                  }}
                  className="btn-primary w-full py-3 text-sm"
                >
                  Create Order
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
